/**
 * Слой i18n (правило №2 CLAUDE.md: ни одной пользовательской строки хардкодом).
 * Механизм — зеркало tr() из TecTactic: работает без контекста UI, фолбэк-цепочка
 * active → en → сам ключ (пропуск виден в UI, а не пустота).
 * Новый язык = новый файл в strings/ + строка в dictionaries.
 * Персист выбора — забота приложения (пакет не трогает localStorage: тесты/SSR).
 */
import { BRAND } from '@app/brand'
import type { b2bRu } from './strings/b2b/ru'
import { en } from './strings/en'
import { et } from './strings/et'
import { ru } from './strings/ru'

export type Lang = 'ru' | 'en' | 'et'
/** Ядро + B2B-домены: типы объединены, но b2b-словари НЕ бандлятся сюда —
 *  их регистрирует только кабинет (initB2bStrings), вьюер остаётся лёгким. */
export type I18nKey = keyof typeof ru | keyof typeof b2bRu

export const LANGS: readonly Lang[] = ['et', 'en', 'ru']

const dictionaries: Record<Lang, Record<string, string>> = { ru, en, et }

/** Доменные словари (b2b и будущие) поверх ядра — merge при регистрации. */
export function registerStrings(lang: Lang, extra: Record<string, string>): void {
  dictionaries[lang] = { ...dictionaries[lang], ...extra }
}

let active: Lang = 'et'
const listeners = new Set<() => void>()

export function tr(key: I18nKey, args?: Record<string, string>): string {
  let raw: string = dictionaries[active][key] ?? dictionaries.en[key] ?? key
  if (args) {
    for (const [k, v] of Object.entries(args)) raw = raw.replaceAll(`{${k}}`, v)
  }
  // Имя продукта подставляется всегда: по правилу №3 бренд живёт только в
  // бренд-конфиге, а строки ссылаются на него плейсхолдером {brand}.
  return raw.includes('{brand}') ? raw.replaceAll('{brand}', BRAND.productName) : raw
}

export function getLang(): Lang {
  return active
}

export function setLang(lang: Lang): void {
  if (lang === active) return
  active = lang
  for (const cb of listeners) cb()
}

/** Подписка на смену языка; возвращает отписку. */
export function onLangChange(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/** Есть ли ключ в словаре (для динамических ключей вида `cat.${category}`). */
export function hasKey(key: string): key is I18nKey {
  return key in dictionaries.en
}

/** Чистая функция выбора языка: сохранённый → языки устройства → et.
 *  Умолчание — эстонский: продукт эстонский, первые площадки и заказчик —
 *  Таллинн; браузер с явным ru/en/et перебивает умолчание. */
export function detectLang(saved: string | null, deviceLangs: readonly string[]): Lang {
  if (saved && (LANGS as readonly string[]).includes(saved)) return saved as Lang
  for (const dl of deviceLangs) {
    const short = dl.slice(0, 2).toLowerCase()
    if ((LANGS as readonly string[]).includes(short)) return short as Lang
  }
  return 'et'
}

/** «420 м» / «1.2 км» — единицы через словарь. */
export function formatDistance(meters: number): string {
  if (meters < 950) return `${Math.round(meters)} ${tr('unit.m')}`
  return `${(meters / 1000).toFixed(1)} ${tr('unit.km')}`
}

/** «6 мин» / «<1 мин». */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return tr('unit.lessMin')
  return `${Math.round(seconds / 60)} ${tr('unit.min')}`
}
